/**
 * Grouping pass that turns collision solids into the few batches the renderer
 * actually draws.
 *
 * Split out of `scene.ts` and kept free of Three.js for one reason: the rule
 * that decides *which* solid depicts *which* prop is a position-dependent
 * contract with `mapfmt.mapSolids`, and getting it wrong shifts every prop's
 * appearance by one without changing a single collision box. That is invisible
 * to the physics tests and invisible to a screenshot unless you already know
 * what the level is supposed to look like. Here it is a pure function over
 * plain data, so it can be asserted on directly.
 *
 * This module decides grouping only, never appearance. `scene.ts` owns the
 * mapping from a batch to a material — that keeps the colour lookup (and later,
 * the mesh lookup) in the file that has Three.js in scope, and keeps this one
 * testable under `environment: "node"`.
 */

import type { GameMap, PropCatalogue, PropDef, StaticWorld } from "@prop-hunt/shared";
import { ceilingHeight } from "./decor.js";

/** An axis-aligned box in the form the renderer wants: centre plus extents. */
export interface RenderBox {
  cx: number;
  cy: number;
  cz: number;
  w: number;
  h: number;
  d: number;
}

/**
 * `floor`, `cover` and `block` are all authored level geometry, split only
 * because they are shaded differently — see `GameRenderer.levelMaterial`, which
 * gives each one a surface. `prop` is one batch per catalogue entry, which is
 * also the granularity a real model would load at.
 */
export type BatchKind = "floor" | "cover" | "block" | "prop";

/**
 * How far below the ceiling a solid can top out and still be a wall.
 *
 * Only has to absorb float noise: walls in both shipped maps are authored at a
 * single height, and cover is roughly a third of it.
 */
const WALL_TOP_TOLERANCE = 0.01;

export interface RenderBatch {
  kind: BatchKind;
  /** The catalogue entry these boxes depict; `null` for level geometry. */
  def: PropDef | null;
  boxes: RenderBox[];
}

/**
 * Every solid in `world`, bucketed so that one bucket can be drawn in one call.
 *
 * The geometry still comes from `world.solids` and nowhere else. The map and
 * catalogue are consulted only to decide which bucket a solid belongs in, which
 * is what keeps drift between the box you see and the box you hit structurally
 * impossible: there is no second derivation of a prop's box here that could
 * disagree with the one movement queries.
 *
 * `mapSolids` appends one solid per placed prop after every authored solid, in
 * order, skipping props whose id is not in the catalogue. So the tail of the
 * array is the props, and `placedPropDefs` reproduces that same skip rule to
 * line up with it. Both halves of that have to agree or the props are drawn
 * shifted by one.
 */
export function renderBatches(
  world: StaticWorld,
  map: GameMap,
  catalogue: PropCatalogue,
): RenderBatch[] {
  const propDefs = placedPropDefs(map, catalogue);
  const firstPropSolid = world.solids.length - propDefs.length;

  const floor: RenderBox[] = [];
  const cover: RenderBox[] = [];
  const block: RenderBox[] = [];
  const byProp = new Map<number, RenderBatch>();

  // Walls are whatever reaches the ceiling, and the ceiling is the top of the
  // tallest solid — the same derivation `decor.ceilingHeight` hangs the lid at,
  // reused rather than restated so a wall cannot be one thing to the renderer's
  // roof and another to its shader.
  const storey = ceilingHeight(world);

  world.solids.forEach((solid, index) => {
    const box: RenderBox = {
      cx: (solid.minX + solid.maxX) / 2,
      cy: (solid.minY + solid.maxY) / 2,
      cz: (solid.minZ + solid.maxZ) / 2,
      w: solid.maxX - solid.minX,
      h: solid.maxY - solid.minY,
      d: solid.maxZ - solid.minZ,
    };

    const def = index >= firstPropSolid ? propDefs[index - firstPropSolid] : undefined;
    if (def !== undefined) {
      let batch = byProp.get(def.id);
      if (batch === undefined) {
        batch = { kind: "prop", def, boxes: [] };
        byProp.set(def.id, batch);
      }
      batch.boxes.push(box);
      return;
    }

    // Anything rooted below y=0 is ground. That is the floor slab on a flat
    // level and a raised deck's column on one with terrain — maptool emits
    // both the same way, reaching up from under the floor to the surface you
    // walk on, and neither is furniture however high its top ends up. Above
    // it, anything that stops short of the ceiling is a desk run rather than a
    // partition: you can see over it and shoot over it, and it is surfaced as
    // furniture instead of getting a wall's skirting board at its base.
    if (solid.minY < 0) floor.push(box);
    else if (solid.maxY < storey - WALL_TOP_TOLERANCE) cover.push(box);
    else block.push(box);
  });

  const batches: RenderBatch[] = [];
  if (floor.length > 0) batches.push({ kind: "floor", def: null, boxes: floor });
  if (cover.length > 0) batches.push({ kind: "cover", def: null, boxes: cover });
  if (block.length > 0) batches.push({ kind: "block", def: null, boxes: block });
  batches.push(...byProp.values());
  return batches;
}

/**
 * The `PropDef` behind each prop solid, in the order `mapSolids` appends them.
 *
 * Mirrors that function's treatment of an id the catalogue does not contain:
 * it contributes no solid, so it must contribute no entry here either, or every
 * prop after it would be drawn wearing the wrong prop's colours.
 */
export function placedPropDefs(map: GameMap, catalogue: PropCatalogue): PropDef[] {
  const defs: PropDef[] = [];
  for (const placed of map.props) {
    const def = catalogue.get(placed.prop);
    if (def !== undefined) defs.push(def);
  }
  return defs;
}

/** One catalogue kind's worth of live prop instances, for `propInstanceGroups`. */
export interface PropInstanceGroup {
  def: PropDef;
  /**
   * `propIndex` (an index into `GameMap.props`) for each instance slot, in
   * slot order. This is the order `GameRenderer` allocates an `InstancedMesh`
   * in, so a `propIndex` looked up here is exactly the slot a later pose
   * update for that index has to write into.
   */
  propIndices: number[];
}

/**
 * Every catalogued placed prop, grouped by kind — the layout `GameRenderer`
 * builds its per-kind `InstancedMesh`es from.
 *
 * A prop's *appearance* no longer comes from static map data (see the M3
 * rendering note in scene.ts: every prop, possessed or not, now arrives as a
 * live entity every tick, which is what closes the possession gap). What
 * stays fixed for the life of a level is which instance *slot* a given
 * `propIndex` occupies and which kind's batch it belongs to — a prop cannot
 * change shape, only position — so that layout is computed once, here, and
 * reused for every pose update afterward.
 *
 * Same skip rule as `placedPropDefs` and `mapSolids`: an uncatalogued prop id
 * contributes no instance, or every prop after it would claim the wrong slot.
 */
export function propInstanceGroups(map: GameMap, catalogue: PropCatalogue): PropInstanceGroup[] {
  const byId = new Map<number, PropInstanceGroup>();
  map.props.forEach((placed, propIndex) => {
    const def = catalogue.get(placed.prop);
    if (def === undefined) return;
    let group = byId.get(def.id);
    if (group === undefined) {
      group = { def, propIndices: [] };
      byId.set(def.id, group);
    }
    group.propIndices.push(propIndex);
  });
  return [...byId.values()];
}
